import React, { useMemo } from 'react';
import moment from 'moment';
import { reviewsFormat, yAxleFormat } from '@op/utils/value';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import { channelInfo } from '@op/components/ChannelLabel';
import { useIntl } from 'umi';
import { getChartPositionTop } from '../utils';

type EChartsOptions = echarts.EChartsOption;

export interface GrowthOfFollowerType {
  // chart的高度
  height?: number;
  // x轴数据
  xAxisData: string[];
  loading?: boolean;
  // 实例数据，每一条线
  seriesDataArray: Array<{
    data: Array<any>;
    seriesOptions?: EChartsOptions['series'];
    color: string;
    name: string;
  }>;
}

export default ({
  height,
  xAxisData,
  seriesDataArray,
  loading,
}: GrowthOfFollowerType) => {
  const { formatMessage } = useIntl();
  // 设置图形配置
  const makeupOptions = () => {
    const seriesData: any = [];
    const totals: Array<number> = [];
    seriesDataArray.forEach(
      ({ data, seriesOptions = {}, color, name }) => {
        // const bgColor = hexToRgba(color, 0.5);
        const seriesItem: any = {
          data,
          type: 'bar',
          name,
          itemStyle: {
            color,
            // borderColor: color,
            // borderWidth: 1,
          },
          barMaxWidth: 15,
          stack: 'total',
          ...seriesOptions,
        };

        seriesData.push(seriesItem);

        data.forEach((item, index) => {
          totals[index] = (item || 0) + (totals[index] || 0);
        });
      },
    );

    function getMaxValue(value:  { max: number; min: number; }) {
      const max = Math.abs(value.max);
      const min  = Math.abs(value.min);
      const newValue = Math.max(max, min);
      if(newValue < 1000) {
        return newValue;
      }   
      return Math.ceil(newValue / 1000) * 1000;
    }

    const yAxis = {
      min (value: { max: number; min: number; }) {
        return getMaxValue(value) * -1;
      },
      max (value: { max: number; min: number; }) {
        return getMaxValue(value);
      },
    };
    const opts: EChartsOptions = {
      tooltip: {
        formatter: (params) => {
          const letTitle = listTimeTitleFunc(params?.[0].axisValue, 'daily');

          return formatChartParamsDate({
            rightTitle: '',
            params,
            isFilterZero: true,
            isSort: true,
            // textShowFun: (marker: string, seriesName: string, element: any) => {
            //   const { name } = channelInfo(seriesName as any);
            //   return `<span class="chart-common-formatter-marker">
            //               <span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${element.borderColor}"></span>
            //           </span>&nbsp;${name}`;
            // },
            listTitleFunc: (element) => {
              const { name } = channelInfo(element.seriesName as any);
              return name;
            },
            dealwithRecords(records, total) {
              const obj: any = {
                text: formatMessage({ id: 'Sum' }),
                value: reviewsFormat(total),
              };
              records.unshift(obj);
            },
            tablesConfigFunc: (records: Array<any>) => [
              {
                showHeader: true,
                records,
                columns: [
                  {
                    key: 'text',
                    header: letTitle,
                  },
                  {
                    key: 'value',
                    align: 'right',
                    weight: 'bold',
                    header: formatMessage({ id: 'Growth of Followers' }),
                  },
                ],
              },
            ],
          });
        },
        confine: false,
        position: (pos, params, dom, rect, size) => getChartPositionTop(pos, size),
      },
      axisPointer: getAxisPointer((data) => moment(data.value).format('MM-DD')),
      legend: {
        show: false,
      },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        axisLabel: {
          fontSize: 8,
          color: 'rgba(38, 40, 50, 0.6)',
          formatter: (value) => moment(value).format('MM-DD'),
        },
        data: xAxisData,
      },
      yAxis: {
        type: 'value',
        splitNumber: 4,
        splitLine: {
          show: true,
          interval: 1,
          lineStyle: {
            type: 'dashed',
            color: '#E5E5E5',
          },
        },
        axisLabel: {
          fontSize: 8,
          color: 'rgba(0, 0, 0, 0.6)',
          showMaxLabel: true,
          showMinLabel: true,
          formatter: (value) => yAxleFormat(value),
        },
        ...yAxis,
      },
      grid: {
        top: 15,
        bottom: 0,
      },
      series: seriesData,
    };

    return opts;
  };

  const options = useMemo(() => makeupOptions(), [seriesDataArray, xAxisData, formatMessage]);

  return (
    <EChartCommon
      optionsProps={{ type: 'bar', excludeOptions: ['axisPointer'] }}
      height={height}
      options={options}
      loading={loading}
      empty={xAxisData.length === 0}
      hasEmptyDesc={false}
    ></EChartCommon>
  );
};
